import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Boxes,
  Briefcase,
  Building,
  Building2,
  Clock,
  FilePlus,
  FileText,
  Gauge,
  KeyRound,
  Languages,
  LayoutDashboard,
  LayoutTemplate,
  LifeBuoy,
  LineChart,
  type LucideIcon,
  Map,
  MessageSquare,
  Network,
  Search,
  Server,
  Settings,
  Shield,
  UploadCloud,
  Webhook,
  Wrench,
  Zap,
} from '@/components/icons';

/**
 * W16-F1-S1 — sidebar nav taxonomy.
 *
 * Pure data + types. The components that render this (W16-F1-S2 group
 * shell, W16-F1-S3 wiring) are kept separate so this file has no React
 * dependency and can be unit-tested as plain config.
 */

export interface NavItem {
  to: string;
  i18nKey: string;
  Icon: LucideIcon;
  /** Stable selector for the W10-F3-S1 onboarding tour. */
  tourId?: string;
  /** Optional org-level feature flag — checked by S3. */
  featureFlag?: string;
  /**
   * #892 P0-006 — nav items tagged `adminOnly: true` only render when
   * the authenticated user holds a partner_admin / customer_admin /
   * platform_admin role. The filter lives in `<NavGroup>` so the same
   * gate applies to every group; the items themselves stay declarative.
   */
  adminOnly?: boolean;
}

export type NavGroupId = 'workspace' | 'insights' | 'operations' | 'admin';

export interface NavGroup {
  id: NavGroupId;
  i18nKey: string;
  items: readonly NavItem[];
}

const WORKSPACE_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboard',
    i18nKey: 'nav.dashboard',
    Icon: LayoutDashboard,
    tourId: 'dashboards-link',
  },
  {
    to: '/buildings',
    i18nKey: 'nav.buildings',
    Icon: Building2,
    tourId: 'buildings-link',
  },
  { to: '/buildings/map', i18nKey: 'nav.buildingsMap', Icon: Map },
  { to: '/portfolios', i18nKey: 'nav.portfolios', Icon: Briefcase },
  { to: '/orgs', i18nKey: 'nav.orgs', Icon: Network },
];

const INSIGHTS_ITEMS: readonly NavItem[] = [
  {
    to: '/dashboards/executive',
    i18nKey: 'nav.executiveDashboard',
    Icon: Gauge,
  },
  {
    to: '/dashboards/carbon',
    i18nKey: 'nav.carbonDashboard',
    Icon: Activity,
  },
  { to: '/analytics', i18nKey: 'nav.analytics', Icon: BarChart3 },
  // W60-F5-S2 (#304) — workspace-level valuation index.
  { to: '/valuation', i18nKey: 'nav.valuation', Icon: LineChart },
  {
    to: '/reports',
    i18nKey: 'nav.reports',
    Icon: FileText,
    tourId: 'tasks-link',
  },
  { to: '/reports/builder', i18nKey: 'nav.reportsBuilder', Icon: FilePlus },
  { to: '/notifications', i18nKey: 'nav.notifications', Icon: Bell },
  // W47-F7-S1 — customer-facing support page (admin triage is a separate
  // entry under the Admin group, gated by `adminOnly`).
  { to: '/support', i18nKey: 'nav.support', Icon: LifeBuoy },
  // W137-F6-S2 — AI compliance assistant (partner-admin only).
  {
    to: '/compliance/assistant',
    i18nKey: 'nav.complianceAssistant',
    Icon: MessageSquare,
    adminOnly: true,
  },
];

/**
 * Operations routes all require a building / measure / incident id and
 * are excluded from the static sidebar list. W16-F1-S3 resolves the
 * active context (building id from `useParams`, etc.) and renders these
 * in the detail-page sub-nav. Listed here so the taxonomy + i18n keys
 * remain the single source of truth.
 */
export const OPERATIONS_DETAIL_ITEMS: readonly NavItem[] = [
  {
    to: '/buildings/:id/operations',
    i18nKey: 'nav.buildingOperations',
    Icon: Activity,
  },
  {
    to: '/buildings/:id/incidents',
    i18nKey: 'nav.buildingIncidents',
    Icon: AlertTriangle,
  },
  { to: '/buildings/:id/sla', i18nKey: 'nav.buildingSla', Icon: Clock },
  { to: '/buildings/:id/permits', i18nKey: 'nav.buildingPermits', Icon: Shield },
  { to: '/buildings/:id/energy', i18nKey: 'nav.buildingEnergy', Icon: Zap },
  { to: '/measures/:id/mv', i18nKey: 'nav.measureMv', Icon: Search },
  {
    to: '/incidents/findings/:id',
    i18nKey: 'nav.inspectionFinding',
    Icon: AlertTriangle,
  },
  {
    to: '/incidents/obligations/:id',
    i18nKey: 'nav.complianceObligation',
    Icon: Shield,
  },
  { to: '/permits/:id', i18nKey: 'nav.permit', Icon: Shield },
  { to: '/work-orders/:id', i18nKey: 'nav.workOrder', Icon: Wrench },
];

const ADMIN_ITEMS: readonly NavItem[] = [
  { to: '/admin', i18nKey: 'nav.admin', Icon: LayoutDashboard, adminOnly: true },
  // #1647 — AI-assisted data ingestion front door. Flag-gated so it only
  // appears for orgs in the design-partner rollout.
  {
    to: '/admin/ingest',
    i18nKey: 'nav.adminIngest',
    Icon: UploadCloud,
    adminOnly: true,
    featureFlag: 'ai.document_classification',
  },
  // W20-F1-S1 — partner-admin modules list (drag-reorder + visibility)
  { to: '/admin/modules', i18nKey: 'nav.adminModules', Icon: Boxes, adminOnly: true },
  // W20-F3-S3 — module-template authoring (drag-drop nested editor).
  // #1708 — the former BREEAM-templates index is now this same surface
  // viewed through the BREEAM framework filter (`?framework=breeam`), so it
  // no longer gets its own nav item.
  { to: '/admin/templates', i18nKey: 'nav.adminModuleTemplates', Icon: LayoutTemplate, adminOnly: true },
  { to: '/admin/imports', i18nKey: 'nav.adminImports', Icon: Activity, adminOnly: true },
  {
    to: '/admin/translations',
    i18nKey: 'nav.adminTranslations',
    Icon: Languages,
    adminOnly: true,
  },
  {
    to: '/admin/integrations/webhooks',
    i18nKey: 'nav.adminWebhooks',
    Icon: Webhook,
    adminOnly: true,
  },
  // W73-F4-S2 — carrier-API admin connection-management UX.
  {
    to: '/admin/integrations/carrier-apis',
    i18nKey: 'nav.adminCarrierApis',
    Icon: Network,
    adminOnly: true,
  },
  // W20-F6-S1 — api-keys grouping audit. The api-keys page itself shipped in
  // W11-F9-S1 (PR #152); F6 reconciles the W20 wave by surfacing the entry in
  // the Authoring & Settings cluster alongside webhooks / templates.
  {
    to: '/admin/api-keys',
    i18nKey: 'nav.adminApiKeys',
    Icon: KeyRound,
    adminOnly: true,
  },
  { to: '/admin/system', i18nKey: 'nav.adminSystem', Icon: Server, adminOnly: true },
  {
    to: '/admin/support/tickets',
    i18nKey: 'nav.adminSupportTickets',
    Icon: LifeBuoy,
    adminOnly: true,
  },
  {
    to: '/admin/partner-settings',
    i18nKey: 'nav.adminPartnerSettings',
    Icon: Building,
    adminOnly: true,
  },
  // W146-F3-S1 — partner analytics dashboard.
  {
    to: '/admin/partner-analytics',
    i18nKey: 'nav.adminPartnerAnalytics',
    Icon: BarChart3,
    adminOnly: true,
  },
  { to: '/settings', i18nKey: 'nav.settings', Icon: Settings },
];

export const NAV_GROUPS: readonly NavGroup[] = [
  { id: 'workspace', i18nKey: 'nav.groups.workspace', items: WORKSPACE_ITEMS },
  { id: 'insights', i18nKey: 'nav.groups.insights', items: INSIGHTS_ITEMS },
  { id: 'operations', i18nKey: 'nav.groups.operations', items: [] },
  { id: 'admin', i18nKey: 'nav.groups.admin', items: ADMIN_ITEMS },
];
